/**
 * dawhub web — entry point + route dispatcher.
 *
 * Routes:
 *   /                → landing page (dawhub product, waitlist-driven)
 *   /flpdiff         → open-source CLI + embedded web viewer
 *   /diff            → standalone viewer shell (drop two .flp files)
 *   /diff/{slug}     → standalone viewer + auto-load demo pair
 *   #/diff           → legacy hash form of /diff
 *   #/diff/{slug}    → legacy hash form of /diff/{slug}
 *
 * Anchor hashes like `#how`, `#faq`, `#waitlist`, `#viewer` scroll the
 * current page and do not change route.
 */

import { compareProjects } from "flpdiff";
import { getState, subscribe, clearError, reset, swap, type State } from "./state.ts";
import { renderDropzone, initDragDrop } from "./dropzone.ts";
import { renderProjectInfo } from "./render-info.ts";
import { renderDiff } from "./render-diff.ts";
import { findDemo, loadDemo, routeSlugFromLocation } from "./demos.ts";
import { renderLanding, initLandingFx } from "./landing.ts";
import { renderFlpdiffPage } from "./flpdiff-page.ts";
import { renderFlpinfoPage, paintFlpinfo } from "./flpinfo-page.ts";
import { renderNav, renderFooter } from "./page-shared.ts";
import { h, $, empty, installClampDelegate } from "./ui.ts";

type Route =
  | { kind: "landing" }
  | { kind: "flpdiff" }
  | { kind: "flpinfo" }
  | { kind: "viewer" }
  | { kind: "demo"; slug: string };

function mount(): void {
  installClampDelegate();
  routeAndRender();
  subscribe(() => {
    // State changes only repaint viewer slots; marketing chrome on
    // landing / flpdiff is static and shouldn't get torn down.
    const k = currentRoute().kind;
    if (k === "landing") return;
    renderViewer(getState());
  });
  window.addEventListener("hashchange", routeAndRender);
  window.addEventListener("popstate", routeAndRender);
}

function currentRoute(): Route {
  const slug = routeSlugFromLocation();
  if (slug) return { kind: "demo", slug };
  const hash = window.location.hash;
  if (hash === "#/diff" || hash === "#/diff/" || hash.startsWith("#/diff?")) {
    return { kind: "viewer" };
  }
  const path = window.location.pathname;
  if (path === "/diff" || path === "/diff/") return { kind: "viewer" };
  if (path === "/flpdiff" || path === "/flpdiff/") return { kind: "flpdiff" };
  if (path === "/flpinfo" || path === "/flpinfo/") return { kind: "flpinfo" };
  return { kind: "landing" };
}

function routeAndRender(): void {
  const route = currentRoute();
  const root = $("#app");
  updatePageMeta(route.kind);

  if (route.kind === "landing") {
    empty(root);
    const tree = renderLanding();
    root.appendChild(tree);
    initLandingFx(tree);
    return;
  }

  if (route.kind === "flpdiff") {
    if (!root.querySelector(".flpdiff-page")) {
      empty(root);
      root.appendChild(renderFlpdiffPage());
      initDragDrop();
    }
    renderViewer(getState());
    return;
  }

  if (route.kind === "flpinfo") {
    if (!root.querySelector(".flpinfo-page")) {
      empty(root);
      root.appendChild(renderFlpinfoPage());
    }
    paintFlpinfo();
    return;
  }

  // Viewer (or demo). Mount the standalone viewer shell if it isn't there yet.
  if (!root.querySelector(".shell")) {
    empty(root);
    root.appendChild(viewerShell());
    initDragDrop();
  }
  renderViewer(getState());
  if (route.kind === "demo") {
    void loadDemoFromRoute(route.slug);
  }
}

async function loadDemoFromRoute(slug: string): Promise<void> {
  const pair = findDemo(slug);
  if (!pair) {
    console.warn(`No demo registered for slug: ${slug}`);
    return;
  }
  try {
    await loadDemo(pair);
  } catch (e) {
    console.error("Failed to load demo", e);
  }
}

function viewerShell(): HTMLElement {
  return h("div", { class: "app" }, [
    renderNav("viewer"),
    h("main", { class: "shell" }, [
      h("header", { class: "shell__header" }, [
        h("h1", { class: "shell__title" }, "Diff viewer"),
        h("p", { class: "shell__tag" },
          "Drop two .flp files to see what changed."),
      ]),
      h("section", { id: "dropzone-slot", class: "dropzone-slot" }),
      h("section", { id: "error-slot", class: "error-slot", "aria-live": "polite" }),
      h("section", { id: "info-slot", class: "info-slot", "aria-live": "polite" }),
      h("section", { id: "diff-slot", class: "diff-slot", "aria-live": "polite" }),
    ]),
    renderFooter(),
  ]);
}

function renderViewer(state: State): void {
  const dz = document.querySelector("#dropzone-slot");
  if (!dz) return;
  empty(dz as HTMLElement);
  dz.appendChild(renderDropzone(state));

  renderError(state);
  renderInfoPanels(state);
  renderDiffPanel(state);
}

function renderDiffPanel(state: State): void {
  const slot = document.querySelector("#diff-slot");
  if (!slot) return;
  empty(slot as HTMLElement);
  if (state.phase !== "HAS_BOTH" || !state.a || !state.b) return;
  const result = compareProjects(state.a.project, state.b.project);
  slot.appendChild(renderDiff(result));
}

function renderInfoPanels(state: State): void {
  const slot = document.querySelector("#info-slot");
  if (!slot) return;
  empty(slot as HTMLElement);

  if (state.phase === "HAS_BOTH" && state.a && state.b) {
    const a = renderProjectInfo(state.a.project, state.a.name, state.a.bytes, "before");
    const b = renderProjectInfo(state.b.project, state.b.name, state.b.bytes, "after");
    slot.appendChild(h("div", { class: "info-pair" }, [a, renderInfoActions(), b]));
    return;
  }

  if (state.a) {
    slot.appendChild(
      renderProjectInfo(state.a.project, state.a.name, state.a.bytes, "before"),
    );
  }
  if (state.b) {
    slot.appendChild(
      renderProjectInfo(state.b.project, state.b.name, state.b.bytes, "after"),
    );
  }
}

function renderInfoActions(): HTMLElement {
  const swapBtn = h("button", {
    type: "button",
    class: "info-action",
    "aria-label": "Swap before and after",
    title: "Swap before and after",
    onclick: () => swap(),
  }, [
    h("span", { class: "info-action__icon", "aria-hidden": "true" }, "⇄"),
    h("span", { class: "info-action__label" }, "Swap"),
  ]);
  const resetBtn = h("button", {
    type: "button",
    class: "info-action info-action--reset",
    "aria-label": "Reset and load new files",
    title: "Reset",
    onclick: () => reset(),
  }, [
    h("span", { class: "info-action__icon", "aria-hidden": "true" }, "↺"),
    h("span", { class: "info-action__label" }, "Reset"),
  ]);
  return h(
    "div",
    { class: "info-actions", role: "toolbar", "aria-label": "Compare actions" },
    [swapBtn, resetBtn],
  );
}

function renderError(state: State): void {
  const slot = document.querySelector("#error-slot");
  if (!slot) return;
  empty(slot as HTMLElement);
  if (!state.error) return;
  const close = h("button", {
    type: "button",
    class: "error__close",
    "aria-label": "Dismiss",
    onclick: clearError,
  }, "×");
  const box = h("div", { class: "error", role: "alert" }, [
    h("span", { class: "error__icon", "aria-hidden": "true" }, "!"),
    h("span", { class: "error__msg" }, [
      state.error.name && h("strong", null, state.error.name + ": "),
      state.error.message,
    ]),
    close,
  ]);
  slot.appendChild(box);
}

// ---- per-route meta swap -----------------------------------------

interface PageMeta {
  title: string;
  description: string;
  ogTitle: string;
  ogDescription: string;
  ogImage: string;
  url: string;
}

const META: Record<Route["kind"], PageMeta> = {
  landing: {
    title: "dawhub — AI-native version control for FL Studio",
    description: "Track every save, talk to your project, roll back any change without losing the rest. Built for FL Studio producers.",
    ogTitle: "dawhub — AI-native version control for FL Studio",
    ogDescription: "Track every save. Talk to your project. Roll back any change without losing the rest.",
    ogImage: "https://dawhub.app/og-image.png",
    url: "https://dawhub.app/",
  },
  flpdiff: {
    title: "flpdiff — open source diff for FL Studio projects · dawhub",
    description: "Semantic diff for FL Studio .flp files. Free, MIT-licensed, runs in your terminal or browser. Powered by dawhub.",
    ogTitle: "flpdiff — open source diff for FL Studio projects",
    ogDescription: "Semantic diff for FL Studio .flp files. Free, MIT-licensed, runs in your terminal or browser.",
    ogImage: "https://dawhub.app/og-image.png",
    url: "https://dawhub.app/flpdiff",
  },
  flpinfo: {
    title: "flpinfo — inspect a FL Studio project · dawhub",
    description: "Drop one .flp file. Get every detail dawhub can extract — including the exact path of every VST that was loaded. Useful for plugin recovery.",
    ogTitle: "flpinfo — inspect a FL Studio project",
    ogDescription: "Drop one .flp file. Read FL version, channels, mixer, and the exact path of every VST that was loaded.",
    ogImage: "https://dawhub.app/og-image.png",
    url: "https://dawhub.app/flpinfo",
  },
  viewer: {
    title: "Diff viewer · dawhub",
    description: "Drop two FL Studio .flp files to see what changed. Files parse locally — nothing uploads.",
    ogTitle: "Diff viewer · dawhub",
    ogDescription: "Drop two FL Studio .flp files to see what changed.",
    ogImage: "https://dawhub.app/og-image.png",
    url: "https://dawhub.app/diff",
  },
  demo: {
    title: "Demo diff · dawhub",
    description: "Walk through a real FL Studio project diff in your browser.",
    ogTitle: "Demo diff · dawhub",
    ogDescription: "Walk through a real FL Studio project diff in your browser.",
    ogImage: "https://dawhub.app/og-image.png",
    url: "https://dawhub.app/diff",
  },
};

function updatePageMeta(kind: Route["kind"]): void {
  const m = META[kind];
  document.title = m.title;
  setMeta("name", "description", m.description);
  setMeta("property", "og:title", m.ogTitle);
  setMeta("property", "og:description", m.ogDescription);
  setMeta("property", "og:url", m.url);
  setMeta("property", "og:image", m.ogImage);
  setMeta("name", "twitter:title", m.ogTitle);
  setMeta("name", "twitter:description", m.ogDescription);
  setMeta("name", "twitter:image", m.ogImage);
  const canonical = document.querySelector('link[rel="canonical"]');
  if (canonical) canonical.setAttribute("href", m.url);
}

function setMeta(attr: "name" | "property", key: string, value: string): void {
  const el = document.querySelector(`meta[${attr}="${key}"]`);
  if (el) el.setAttribute("content", value);
}

// Expose a tiny debug handle for manual testing.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
(globalThis as any).__flpdiff = { getState };

mount();
